import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import styled from "styled-components";
import {
  searchChurch,
  closeSearchChurchModal,
  resetChurchSearchTerm,
  setChurchSearchTerm,
  resetSearchResults,
} from "features/memberships/membershipSlice";
import { ModalContainerMain } from "components/modal/modal.styled";
import ModalCloseButton from "components/buttons/ModalCloseButton";
import ChurchSearchResultsItem from "./ChurchSearchResultsItem";
import { IoSearchOutline } from "react-icons/io5";
import { TbBuildingChurch } from "react-icons/tb";
import { useTranslation } from "react-i18next";

export default function SearchForChurchModal() {
  const { t } = useTranslation("church");
  const dispatch = useDispatch();
  const [searchVal, setSearchVal] = useState("");
  const [searchTermChanged, setSearchTermChanged] = useState(false);

  const {
    churchesSearchResult,
    churchSearchTerm,
    isChurchSearchActive,
    isChurchSearchSuccess,
    isChurchSearchLoading,
    searchChurchCount,
    noMoreResults,
  } = useSelector((state) => state.memberships2);

  const handleInputChange = (e) => {
    if (churchSearchTerm && churchSearchTerm !== e.target.value) {
      setSearchTermChanged(true);
    }
    setSearchVal(e.target.value);
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    if (searchVal === churchSearchTerm)
      return toast.info(t("search.showing", { query: searchVal }));
    if (searchVal.trim().length <= 2) return toast.error(t("search.minimum"));
    if (searchTermChanged) dispatch(resetSearchResults());
    dispatch(setChurchSearchTerm(searchVal));
    dispatch(searchChurch({ searchVal }));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleClearSearch = () => {
    setSearchVal("");
    dispatch(resetChurchSearchTerm());
  };

  const handleLoadMore = () => {
    dispatch(searchChurch({ searchVal, searchChurchCount }));
  };

  const hasResults = isChurchSearchSuccess && churchesSearchResult;

  return (
    <ModalContainerMain>
      <Modal>
        <ModalCloseButton
          handler={() => dispatch(closeSearchChurchModal())}
          top="12px"
          right="12px"
          size="32px"
        />

        {/* Header */}
        <ModalHeader>
          <IconCircle>
            <TbBuildingChurch
              style={{ fontSize: "24px", color: "var(--themeColor)" }}
            />
          </IconCircle>
          <div>
            <ModalTitle>{t("search.title")}</ModalTitle>
            <ModalSub>{t("search.subtitle")}</ModalSub>
          </div>
        </ModalHeader>

        {/* Search bar */}
        <SearchBar onSubmit={handleSearch}>
          <SearchInput
            value={searchVal}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={t("search.placeholder")}
            autoFocus
          />
          <SearchBtn
            type="submit"
            onClick={handleSearch}
            loading={isChurchSearchLoading}
          >
            {isChurchSearchLoading ? (
              <Spinner />
            ) : (
              <IoSearchOutline style={{ fontSize: "18px" }} />
            )}
          </SearchBtn>
        </SearchBar>

        {/* Active search term pill */}
        {isChurchSearchActive && churchSearchTerm && (
          <PillRow>
            <TermPill>
              "{churchSearchTerm}"
              <ClearBtn onClick={handleClearSearch}>✕</ClearBtn>
            </TermPill>
            {hasResults && (
              <ResultCount>
                {churchesSearchResult.length === 0
                  ? t("search.noResults")
                  : t("search.results", { count: churchesSearchResult.length })}
              </ResultCount>
            )}
          </PillRow>
        )}

        {/* Results */}
        <ResultsArea>
          {isChurchSearchLoading ? (
            <EmptyState>
              <Spinner large />
              <span>{t("search.searching")}</span>
            </EmptyState>
          ) : !isChurchSearchActive ? (
            <EmptyState>
              <TbBuildingChurch style={{ fontSize: "40px", opacity: 0.25 }} />
              <span>{t("search.prompt")}</span>
            </EmptyState>
          ) : hasResults && churchesSearchResult.length === 0 ? (
            <EmptyState>
              <span style={{ fontSize: "32px" }}>🔍</span>
              <span>{t("search.noneFor", { query: churchSearchTerm })}</span>
              <TryAgain onClick={handleClearSearch}>
                {t("search.tryDifferent")}
              </TryAgain>
            </EmptyState>
          ) : hasResults ? (
            <>
              {churchesSearchResult.map((church) => (
                <ChurchSearchResultsItem key={church._id} data={church} />
              ))}
              {!noMoreResults && churchesSearchResult.length >= 5 && (
                <LoadMoreBtn onClick={handleLoadMore}>
                  {t("search.loadMore")}
                </LoadMoreBtn>
              )}
            </>
          ) : null}
        </ResultsArea>
      </Modal>
    </ModalContainerMain>
  );
}

/* ── Styled components ─────────────────────────────────────────────────────── */

const Modal = styled.div`
  background: #fff;
  width: 480px;
  max-width: 95vw;
  max-height: 85vh;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);

  @media (max-width: 520px) {
    width: 100%;
    border-radius: 16px 16px 0 0;
    max-height: 90vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 22px 24px 16px;
  border-bottom: 1px solid #f0f0f0;
`;

const IconCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: rgba(0, 180, 204, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  font-size: 17px;
  font-weight: 700;
  color: var(--lightThemeFontColor);
  margin: 0 0 2px;
`;

const ModalSub = styled.p`
  font-size: 12px;
  color: #888;
  margin: 0;
`;

const SearchBar = styled.form`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 16px 20px 8px;
  border: 1.5px solid #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
  background: #fafafa;
  transition: border-color 0.15s;

  &:focus-within {
    border-color: var(--themeColor);
    background: #fff;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 12px 14px;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #333;

  &::placeholder {
    color: #aaa;
  }
`;

const SearchBtn = styled.button`
  padding: 0 16px;
  height: 44px;
  border: none;
  background: ${(p) => (p.loading ? "#ccc" : "var(--themeColor)")};
  color: #fff;
  cursor: ${(p) => (p.loading ? "wait" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  flex-shrink: 0;

  &:hover {
    background: ${(p) => (p.loading ? "#ccc" : "#009ab0")};
  }
`;

const PillRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 20px 8px;
`;

const TermPill = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 20px;
  background: rgba(0, 180, 204, 0.1);
  color: var(--themeColor);
  font-size: 12px;
  font-weight: 600;
`;

const ClearBtn = styled.span`
  cursor: pointer;
  opacity: 0.65;
  font-size: 11px;
  &:hover {
    opacity: 1;
  }
`;

const ResultCount = styled.span`
  font-size: 12px;
  color: #999;
  margin-left: auto;
`;

const ResultsArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 12px 16px;

  ::-webkit-scrollbar {
    width: 4px;
  }
  ::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 4px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px 20px;
  color: #999;
  font-size: 13px;
  text-align: center;
`;

const TryAgain = styled.span`
  color: var(--themeColor);
  font-size: 13px;
  cursor: pointer;
  font-weight: 600;
  &:hover {
    text-decoration: underline;
  }
`;

const LoadMoreBtn = styled.button`
  width: 100%;
  padding: 10px;
  margin-top: 8px;
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
  background: #fafafa;
  color: var(--lightThemeFontColor);
  font-size: 13px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.15s;

  &:hover {
    background: #f0f0f0;
  }
`;

const Spinner = styled.div`
  width: ${(p) => (p.large ? "28px" : "16px")};
  height: ${(p) => (p.large ? "28px" : "16px")};
  border: 2px solid ${(p) => (p.large ? "#ddd" : "rgba(255,255,255,0.4)")};
  border-top-color: ${(p) => (p.large ? "var(--themeColor)" : "#fff")};
  border-radius: 50%;
  animation: spin 0.7s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
